import { transformKeysToPascalCase } from '@/Utils/ObjectUtility.js';

import {
    projectPhaseSearchType,
    projectSearchSortType,
    userProjectInteractionTypes,
} from '@/Models';

import {
    enumToSelectOptions,
    mapEnumDisplayNameFromResource,
} from '@/Utils/EnumUtility';

import { fieldTypes } from '@/Types/FieldTypes';

import Resources from '@/Resources.js';

export const filterProjectSearchTypes = {
    projectPhase: 0,
    creator: 1,
    searchTerm: 2,
    includeArchivedProjects: 3,
};

export function getFilterGroupDisplayName(filterSearchType) {
    const filterGroupDisplayNames = {
        [filterProjectSearchTypes.projectPhase]: Resources.Search.SearchFiltersGroupStatusLabel,
        [filterProjectSearchTypes.creator]: Resources.Search.SearchFiltersGroupCreatorLabel,
        [filterProjectSearchTypes.searchTerm]: Resources.Search.SearchFiltersGroupSearchPhraseLabel,
        [filterProjectSearchTypes.includeArchivedProjects]: Resources.BackerCenter.ArchivedProjectsFilterLabel,
    };
    return filterGroupDisplayNames[filterSearchType] || '';
}

export function buildDefaultProjectsFilterParams(params = {}) {
    const formattedParams = transformKeysToPascalCase(params);
    return {
        projectInteractionTypes: formattedParams.projectInteractionTypes
            || [userProjectInteractionTypes.backed, userProjectInteractionTypes.followed],
        creatorID: formattedParams.creatorID || null,
        creatorName: formattedParams.creatorName || null,
        sortType: formattedParams.sortType || 0,
        term: formattedParams.term || null,
        projectPhaseSearchTypes: formattedParams.projectPhaseSearchTypes || [],
        creator: {
            creatorID: formattedParams.creatorID || null,
            name: formattedParams.creatorName || null,
        },
        includeArchivedProjects: formattedParams.includeArchivedProjects ?? null,
    };
}

export function buildProjectsCurrentFilter(searchParams) {
    const config = [
        ...(searchParams.projectPhaseSearchTypes || []).map(value => ({
            key: projectPhaseSearchType[value].name,
            label: projectPhaseSearchType[value].displayName,
            group: getFilterGroupDisplayName(filterProjectSearchTypes.projectPhase),
            filterSearchType: filterProjectSearchTypes.projectPhase,
            value,
            isEnabled: true,
        })),
        {
            key: 'creator',
            label: searchParams.creatorName,
            group: getFilterGroupDisplayName(filterProjectSearchTypes.creator),
            filterSearchType: filterProjectSearchTypes.creator,
            isEnabled: !!searchParams.creatorID,
        },
        {
            key: 'searchTerm',
            label: searchParams.term,
            group: getFilterGroupDisplayName(filterProjectSearchTypes.searchTerm),
            filterSearchType: filterProjectSearchTypes.searchTerm,
            isEnabled: !!searchParams.term,
        },
        {
            isEnabled: searchParams.includeArchivedProjects === true,
            key: 'includeArchivedProjects',
            label: Resources.BackerCenter.IncludeArchivedProjectsLabel,
            filterSearchType: filterProjectSearchTypes.includeArchivedProjects,
        },
    ];

    return config
        .filter(f => f.isEnabled)
        .map(({ key, label, group, filterSearchType, value }) => ({
            key,
            label,
            group,
            filterSearchType,
            value: value ?? null,
        }));
}

export function removeProjectsSelectedFilter(filter, searchParams) {
    const result = { ...searchParams };
    const filterValue = v => v !== filter.value;

    const strategies = {
        [filterProjectSearchTypes.projectPhase]: () => {
            result.projectPhaseSearchTypes = result.projectPhaseSearchTypes.filter(filterValue);
        },
        [filterProjectSearchTypes.creator]: () => {
            result.creatorID = null;
            result.creatorName = null;
            result.creator = { creatorID: null, name: null };
        },
        [filterProjectSearchTypes.searchTerm]: () => {
            result.term = null;
        },
        [filterProjectSearchTypes.includeArchivedProjects]: () => {
            result.includeArchivedProjects = null;
        },
    };

    const strategy = strategies[filter.filterSearchType];
    if (strategy && typeof strategy === 'function') {
        strategy();
    }

    return result;
}

export function buildProjectSortSchemaList() {
    return [
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'sortType',
            label: Resources.Search.SearchProjectSortGroupLabel,
            options: enumToSelectOptions(
                mapEnumDisplayNameFromResource(Resources.Common, projectSearchSortType),
                e => e.value === projectSearchSortType.alphabeticalAsc
                || e.value === projectSearchSortType.alphabeticalDesc
                || e.value === projectSearchSortType.default,
            ),
            props: {
                type: 'radio',
                showLabel: false,
            },
        },
    ];
}

export function buildProjectsFilterList(searchCreatorPromise) {
    return [
        {
            type: fieldTypes.checkboxGroup,
            modelName: 'projectPhaseSearchTypes',
            label: Resources.Search.SearchProjectsFiltersStatusLabel,
            options: enumToSelectOptions(
                mapEnumDisplayNameFromResource(Resources.Search, projectPhaseSearchType),
                () => true,
                (a, b) => a.order - b.order,
            ),
            qa: 'ProjectStatus',
        },
        {
            type: fieldTypes.autocomplete,
            modelName: 'creator',
            label: Resources.Search.SearchProjectsFiltersCreatorLabel,
            placeholder: Resources.Search.SearchProjectsFiltersCreatorPlaceholder,
            searchFn: searchCreatorPromise,
            valueComparer: (a, b) => a.name === b.name,
            minChars: 3,
            maxInputLength: 55,
            onInput: (val, model) => {
                if (!val) {
                    model.creatorName = null;
                    model.creatorID = null;
                    model.creator = null;
                }
            },
            qa: 'Creator',
        },
        {
            type: fieldTypes.text,
            modelName: 'term',
            rules: {
                max: 80,
            },
            label: Resources.Search.SearchProjectsFiltersSearchPhraseLabel,
            placeholder: Resources.Search.SearchProjectsFiltersSearchPhrasePlaceholder,
            qa: 'SearchTerm',
        },
        {
            type: fieldTypes.singleStateCheckboxField,
            modelName: 'includeArchivedProjects',
            label: Resources.BackerCenter.ArchivedProjectsFilterLabel,
            props: {
                customLabelText: Resources.BackerCenter.IncludeArchivedProjectsLabel,
            },
            qa: 'includeArchivedProjects',
        },
    ];
}
